import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DebitNoteDataTable } from './DebitNoteDataTable'
import { columns } from './columns'
import { listDebitNotes } from '@/app/services/debitNoteService'
import { ListQueryParams } from '@/app/types/api'
import { VoucherResponse } from '@/app/types/voucher'
import { toast } from 'sonner'

const DebitNotesPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  // Build query parameters
  const queryParams: ListQueryParams = {
    page: pageIndex + 1, // API pages are 1-indexed
    limit: pageSize,
    search_query: searchQuery || undefined,
    sort_by: 'transaction_date',
    sort_dir: 'desc'
  }

  // Fetch debit notes
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['debitNotes', queryParams],
    queryFn: () => listDebitNotes(queryParams),
    keepPreviousData: true
  })

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value)
    setPageIndex(0) // Reset to first page on search
  }, [])

  // Handle pagination
  const handlePaginationChange = useCallback((newPageIndex: number, newPageSize: number) => {
    setPageIndex(newPageIndex)
    setPageSize(newPageSize)
  }, [])

  // Navigate to create new debit note
  const handleCreateNew = () => {
    navigate('/debit-notes/new')
  }

  // Show error if any
  if (isError) {
    toast.error(`Error loading debit notes: ${(error as Error).message}`)
  }

  const debitNotes = data?.items || []
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Debit Notes</h1>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Create Debit Note
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Debit Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <DebitNoteDataTable
            columns={columns}
            data={debitNotes}
            isLoading={isLoading}
            onSearch={handleSearch}
            pagination={{
              pageIndex,
              pageSize,
              pageCount: totalPages,
              onPaginationChange: handlePaginationChange
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export default DebitNotesPage
